package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.localeFlagPrefix
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import dev.lingvo.shared.FileDto
import dev.lingvo.shared.FileFormat
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Reloadable
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthoritySubject
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectAction
import kotlinx.coroutines.CancellationException

/** Read token describes this private UI fetch, not an immutable server/export snapshot. */
internal data class ExportData(val project: ProjectDetailDto, val files: List<FileDto>, val readToken: Int)

/** Import and export observe one guarded setup read; observers never dispatch a write. */
internal data class ProjectFilesObserver(
    val started: () -> Unit,
    val loaded: (List<FileDto>) -> Unit,
    val failed: (Throwable) -> Unit,
)

internal data class ExportSetupView(
    val subject: ProjectAuthoritySubject?,
    val loaded: Reloadable<ExportData>,
    val token: Int,
    val retry: () -> Unit,
    val retryAllowed: Boolean,
) {
    val stale: Boolean get() = loaded.refreshing || loaded.refreshError != null ||
        (loaded.state as? Async.Success)?.value?.readToken != token
}

@Composable
internal fun rememberExportSetup(
    slug: String,
    subject: ProjectAuthoritySubject?,
    refreshToken: Int,
    observer: ProjectFilesObserver? = null,
    canRetry: () -> Boolean = { true },
): ExportSetupView {
    var retryToken by remember(slug, subject) { mutableStateOf(0) }
    val token = refreshToken + retryToken
    val loaded = loadExportSetup(slug, subject, token, observer)
    return ExportSetupView(subject, loaded, token, retry = {
        // The live guard also protects an enabled button captured before the next composition.
        if (canRetry()) {
            observer?.started?.invoke()
            retryToken++
        }
    }, retryAllowed = canRetry())
}

internal fun captureExportRead(slug: String, subject: ProjectAuthoritySubject?): ProjectActionClaim? =
    ProjectActionClaim.capture(liveProjectRoute(slug), Session.organizations, ProjectPrivilege.READ)
        ?.takeIf { it.subject == subject }

internal suspend fun <T> requestExportRead(claim: ProjectActionClaim, transport: suspend () -> T): T =
    requestProjectAction(claim, { liveProjectRoute(claim.routeSlug) }, { Session.organizations }, transport)

@Composable
internal fun loadExportSetup(slug: String, subject: ProjectAuthoritySubject?, token: Int, observer: ProjectFilesObserver? = null): Reloadable<ExportData> =
    loadReloadable(slug, subject, reloadToken = token) {
        observer?.started?.invoke()
        try {
            val claim = captureExportRead(slug, subject)
                ?: throw ApiException(403, "export_subject_unavailable", tr("export_setup.project_unverified", "This project's export access could not be verified. Open the project again."))
            val expected = liveProjectRoute(slug)!!.project
            val project = requestExportRead(claim) { ApiClient.getProject(slug) }
            if (project.id != expected.id || project.slug != slug || project.orgId != expected.orgId) throw invalidExportSetup()
            // Recheck the actor/role between reads, and before notifying either consumer.
            val files = requestExportRead(claim) { ApiClient.listFiles(slug) }
            if (files.any { it.projectId != project.id || it.id.isBlank() || it.path.isBlank() } ||
                files.map { it.id }.distinct().size != files.size) throw invalidExportSetup()
            observer?.loaded?.invoke(files)
            ExportData(project, files, token)
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: Throwable) {
            observer?.failed?.invoke(failure)
            throw failure
        }
    }

private fun invalidExportSetup() = ApiException(502, "invalid_response",
    tr("export_setup.setup_unverified", "The export setup did not match this project. Retry the file read."))

/** Explicit read recovery; no callback ever repeats import or starts a download. */
@Composable
internal fun ExportSetupFeedback(state: Reloadable<ExportData>, pending: Boolean, onRetry: () -> Unit, retryEnabled: Boolean = true): Boolean {
    val failure = state.refreshError ?: (state.state as? Async.Failure)
    if (failure != null) {
        val inaccessible = failure.isRefusal || failure.isMissing
        LAlert(
            title = when {
                failure.isRefusal -> t("export_setup.no_access", "No access to this project's files")
                failure.isMissing -> t("export_setup.project_unavailable", "This project is unavailable")
                state.state is Async.Success -> t("export_setup.refresh_failed", "Could not refresh export setup")
                else -> t("export_setup.couldnt_load_this_projects_files", "Couldn't load this project's files")
            },
            description = failure.readable(), variant = LAlertVariant.WARNING, live = true,
            action = if (inaccessible) null else {
                { LButton(t("export_setup.retry", "Retry export setup"), onRetry, variant = LButtonVariant.SECONDARY,
                    size = LButtonSize.SM, enabled = retryEnabled) }
            },
        )
        if (!inaccessible && state.state is Async.Success) {
            LMuted(t("export_setup.showing_previous", "Showing the last loaded export setup. Retry to read the current files; do not repeat an acknowledged import."))
        }
        return inaccessible
    }
    if (pending) LMuted(t("export_setup.refreshing", "Checking the updated export setup…"))
    return false
}

/**
 * The two fallbacks, read where they are used rather than held as constants.
 *
 * `tr` and not `t`: both are read inside a `catch` in a coroutine, which is not composition.
 */
internal fun downloadFileFailed(): String =
    tr("export_setup.couldnt_download_this_file_retry", "Couldn't download this file. Retry?")

internal fun downloadBundleFailed(): String =
    tr("export_setup.couldnt_download_this_bundle_retry", "Couldn't download this bundle. Retry?")

@Composable
internal fun MutedCopy(text: String) {
    P(
        Modifier
            .color(ColorMode.current.toSitePalette().textSecondary)
            .fontSize(TypeScale.BodySmall)
            .lineHeight(TypeScale.BodyLineHeight)
            .margin(0.px)
            .toAttrs(),
    ) { Text(text) }
}

/**
 * The last two segments of a path, which is what tells one localization file from another.
 *
 * A leading ellipsis rather than a silent trim: a reader who sees `…/values/strings.xml` knows
 * there is more of it, and the full path is printed under the control whenever this shortens one.
 */
internal fun shortPath(path: String): String {
    val segments = path.split('/').filter { it.isNotEmpty() }
    return if (segments.size <= 2) path else "…/" + segments.takeLast(2).joinToString("/")
}

/** Spoken names and headings need no flag or code; catalogued variants retain their region names. */
internal fun plainLocaleLabel(locale: String): String = localeInfo(locale).englishName

internal fun localeLabel(locale: String, sourceLocale: String): String {
    val info = localeInfo(locale)
    // The whole option is one message: the flag, the language, the code and the parenthetical that
    // marks the source are punctuation decisions a translator has to be able to make together.
    val named = "${localeFlagPrefix(info.code)}${info.englishName}"
    return if (locale == sourceLocale) {
        trf(
            "export_card.locale_option_source",
            "{language} — {code} (source)",
            "language" to named,
            "code" to locale,
        )
    } else {
        trf(
            "export_card.locale_option",
            "{language} — {code}",
            "language" to named,
            "code" to locale,
        )
    }
}

internal fun exportName(slug: String, file: FileDto, locale: String, format: FileFormat): String {
    val original = file.path.substringAfterLast('/')
    val stem = original.substringBeforeLast('.', original)
    return "$slug-$locale-$stem.${format.extension}".replace(Regex("[^A-Za-z0-9._-]"), "-")
}
